import { computed, ref } from 'vue'

// ── El tema: claro, oscuro o el que diga el aparato ──────────────────────────
//
// Tres preferencias y dos temas. La preferencia es lo que la persona elige
// (`light`, `dark` o `system`) y se guarda; el tema es lo que se pinta, y con
// `system` sale del sistema operativo — y lo sigue EN VIVO: el Windows que pasa
// a oscuro al anochecer, el iPhone en modo automático o el Android con horario
// cambian la pantalla sin recargar.
//
// Antes solo había dos, y el sistema se miraba una vez, en el primer arranque:
// en cuanto alguien pulsaba el botón quedaba fijado para siempre, y no había
// forma de volver a «lo que diga el móvil».
//
// El primer pintado lo hace el guion en línea de `platform/web/index.html`, antes
// de que cargue nada, para que quien usa el oscuro no vea un fogonazo blanco. Ese
// guion lee la misma clave y aplica la misma regla: si se cambia una, la otra.
//
// Es una sola instancia por página (el estado vive fuera de la función) porque
// lo usan dos armazones —el ERP y el portal de clientes— y el oyente del sistema
// tiene que ser uno.

export const THEME_KEY = 'general.theme'
export const THEME_PREFERENCES = ['light', 'dark', 'system']

// El color de la barra del navegador en Android, de Safari en iOS y de la
// ventana de la app instalada. Tiene que seguir al tema PINTADO, no al del
// sistema: con el móvil en oscuro y el ERP forzado a claro, una barra negra
// encima de una página blanca parece un fallo.
//
// En claro, el color del CLIENTE cuando se sabe: la marca de la instancia
// (platform/companies, frontend/brand.js) lo deja en `data-brand-color` del
// <html>, y una app instalada con el icono de un cliente verde no puede abrirse
// con la barra azul del producto.
const BAR_COLOR = { light: '#2563eb', dark: '#0b0f14' }

function barColor(theme) {
  if (theme === 'dark') return BAR_COLOR.dark
  const brand = typeof document !== 'undefined' ? document.documentElement.dataset.brandColor : null
  return /^#[0-9a-f]{6}$/i.test(brand ?? '') ? brand : BAR_COLOR.light
}

const preference = ref(read())
const systemDark = ref(false)
let media = null

const resolved = computed(() =>
  preference.value === 'system' ? (systemDark.value ? 'dark' : 'light') : preference.value)

function read() {
  try {
    const stored = localStorage.getItem(THEME_KEY)
    return THEME_PREFERENCES.includes(stored) ? stored : 'system'
  } catch {
    // Safari en privado y algunos navegadores empotrados lanzan al tocar el
    // almacenamiento: sin preferencia guardada, manda el sistema.
    return 'system'
  }
}

function apply() {
  const root = document.documentElement
  if (root.dataset.theme !== resolved.value) root.dataset.theme = resolved.value
  for (const meta of document.querySelectorAll('meta[name="theme-color"]')) {
    meta.setAttribute('content', barColor(resolved.value))
  }
}

function syncSystem() {
  if (!media) return
  systemDark.value = media.matches
  apply()
}

function listen() {
  if (media || typeof window === 'undefined' || !window.matchMedia) return
  media = window.matchMedia('(prefers-color-scheme: dark)')
  // Safari anterior a 14 solo conoce `addListener`.
  if (media.addEventListener) media.addEventListener('change', syncSystem)
  else media.addListener?.(syncSystem)
  // Una app instalada en iOS que vuelve del segundo plano no siempre recibe el
  // `change` que ocurrió mientras dormía: se vuelve a preguntar al despertar.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') syncSystem()
  })
  window.addEventListener('pageshow', syncSystem)
  // Otra pestaña del mismo ERP ha cambiado la preferencia.
  window.addEventListener('storage', (event) => {
    if (event.key !== THEME_KEY) return
    preference.value = read()
    apply()
  })
  syncSystem()
}

// Quien quiere enterarse de que la PERSONA ha elegido —el armazón, que lo guarda
// en su cuenta para que viaje a sus otros aparatos. El ui-kit no sabe de cuentas.
const chosenListeners = new Set()

export function onThemeChosen(fn) {
  chosenListeners.add(fn)
  return () => chosenListeners.delete(fn)
}

function store(value) {
  if (!THEME_PREFERENCES.includes(value)) return false
  preference.value = value
  try {
    // `system` se guarda también, y no como ausencia: así una elección hecha
    // se distingue de una que nunca se hizo si algún día cambia el valor por
    // defecto.
    localStorage.setItem(THEME_KEY, value)
  } catch { /* sin almacenamiento, dura lo que dure la página */ }
  apply()
  return true
}

// Lo que elige la persona: se aplica, se guarda aquí y se avisa.
function setPreference(value) {
  if (store(value)) for (const fn of chosenListeners) fn(value)
}

// Lo que llega de su cuenta, elegido en otro aparato: se aplica y se guarda en
// este navegador para el próximo primer pintado, pero NO se avisa — avisar sería
// devolverle a la cuenta lo que acaba de decir.
export function adoptThemePreference(value) {
  store(value)
}

// Lo que hay guardado en este navegador, sin contar el valor por defecto: `null`
// si aquí nunca se eligió nada.
export function storedThemePreference() {
  try {
    const stored = localStorage.getItem(THEME_KEY)
    return THEME_PREFERENCES.includes(stored) ? stored : null
  } catch { return null }
}

// El botón de la barra de arriba: una pulsación, la siguiente preferencia.
// Claro → oscuro → automático, en el orden de los tres botones del menú.
function cyclePreference() {
  const at = THEME_PREFERENCES.indexOf(preference.value)
  setPreference(THEME_PREFERENCES[(at + 1) % THEME_PREFERENCES.length])
}

export function useTheme() {
  listen()
  return { preference, theme: resolved, setPreference, cyclePreference }
}
